import Prompt from '@components/Prompt'
import { Titlebar } from '@components/Titlebar/Titlebar'
import useStatusPill from '@hooks/useStatusPill'
import { TokenCard } from '@modules/Swap/ExchangeView'
import useLiquidium from '@plug-core/hooks/useLiquidium'
import { isInflow, LiquidiumMode, useLiquidiumAction } from '@plug-core/hooks/useLiquidiumAction'
import { logger } from '@plug-core/logger'
import { Button, ListGroup, ListRow, Text } from '@ui'
import React, { PropsWithChildren, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Portfolio from './Portfolio'
import { useLiquidiumBalances } from './useLiquidiumBalances'

const stringifyWithBigInt = (obj: unknown) => {
  return JSON.stringify(
    obj,
    (_key, value) => (typeof value === 'bigint' ? value.toString() : value),
    2
  )
}

const FundItem = ({
  attribute,
  children,
}: PropsWithChildren & {
  attribute: string
}) => (
  <ListRow
    dense
    primary={
      <Text as="span" variant="body-s" tone="fg">
        {attribute}
      </Text>
    }
    trailing={
      <Text as="span" variant="body-s" tone="fg-2">
        {children}
      </Text>
    }
  />
)

const FundBorrowLend: React.FC = () => {
  const mode = useParams<{ mode: LiquidiumMode }>().mode || 'supply'
  const asset = useSearchParams()[0].get('asset') || undefined
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showStatus } = useStatusPill()

  const [submitError, setSubmitError] = useState<string>()
  const [loading, setLoading] = useState(false)

  const {
    amount,
    setAmount,
    selectedToken,
    setSelectedToken,
    availableTokens,
    tokensToBalances,
    healthFactorImpact,
    properties,
    maxAmount,
    signing,
    error,
    onSubmit,
  } = useLiquidiumAction(mode, asset)

  const { profileId } = useLiquidium()
  const balances = useLiquidiumBalances()

  const handleSubmit = async () => {
    setLoading(true)
    try {
      await onSubmit()

      // Inflows are only sent here, not finished: the protocol credits them minutes later.
      const action = t(`borrowLend.${mode}`)
      showStatus(
        isInflow(mode) ? t('borrowLend.sent', { action }) : t('borrowLend.completed', { action })
      )
    } catch (e) {
      setSubmitError(stringifyWithBigInt(e))
      logger.error(`Failed to execute ${mode} on Liquidium`, { originalError: e })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mt-4 px-5">
      <Titlebar title={t(`borrowLend.${mode}`)} onActionClick={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-1 flex-col gap-2">
          {!!profileId && (
            <div className="mb-2.5">
              <Portfolio
                balances={balances}
                healthFactor={healthFactorImpact.current}
                newHealthFactor={healthFactorImpact.projected}
              />
            </div>
          )}

          <TokenCard
            token={selectedToken}
            amount={amount}
            maxAmount={maxAmount}
            setAmount={setAmount}
            direction="from"
            error={undefined}
            showBalance={mode !== 'borrow'}
            tokensForSelect={availableTokens}
            onTokenSelect={setSelectedToken}
            tokensToBalances={tokensToBalances}
          />

          <ListGroup>
            {properties.map((p) => (
              <FundItem key={p.title} attribute={p.title}>
                {p.value}
              </FundItem>
            ))}
          </ListGroup>
        </div>

        <div className="mt-4">
          <Button
            variant="primary"
            fullWidth
            disabled={!!error || loading}
            loading={loading}
            onClick={handleSubmit}
          >
            {error || (signing && t('borrowLend.signing')) || t(`borrowLend.${mode}`)}
          </Button>
        </div>
      </div>

      <Prompt show={!!submitError} close={() => setSubmitError('')}>
        <div className="flex flex-col items-center">
          <Text align="center" breakAll className="mt-2">
            {submitError}
          </Text>

          <div className="mt-4 flex">
            <Button size="sm" onClick={() => setSubmitError('')} variant="secondary">
              {t('common.understood')}
            </Button>
          </div>
        </div>
      </Prompt>
    </div>
  )
}

export default FundBorrowLend
